"use client"

import { useEffect, useRef, useCallback, useMemo } from "react"
import { useSession, getSession } from "next-auth/react"
import { calculateTokenExpiry } from "../contexts/AuthContext"

/**
 * The issue we try to solve here is updating the oauth access token.
 * We have a refresh token we can use only once.
 *
 * The refresh is triggered by the browser, but the user could have
 * multiple tabs open, and only one refresh can succeed, the others
 * will fail.
 *
 * A successful refresh will update the user cookie.
 *
 * Our approach:
 * 1. Schedule a refresh 5 minutes before token expiry (with random jitter)
 * 2. When refresh time arrives, check if token still needs refreshing
 * 3. If still needed, attempt single refresh (no retries)
 * 4. On success: let useEffect reschedule based on new token
 * 5. On failure: reschedule retry in 10-30 seconds (random)
 */

// Generate unique tab identifier for logging
const generateTabId = () => Math.random().toString(36).substr(2, 8)

const refreshThreshold = process.env["NODE_ENV"] === "development" ? 30 : 300 // 30 seconds in dev, 5 minutes in prod

export function useTokenRefresh() {
  const { data: session, update: updateSession } = useSession()
  const refreshTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
  const failureCountRef = useRef(0)
  const refreshInProgress = useRef(false)

  // Generate stable tab ID for this hook instance
  const tabId = useMemo(() => generateTabId(), [])

  /**
   * Perform the actual refresh, but since it is scheduled we check if
   * another tab hasn't refreshed the token in the mean-time.
   */
  const performRefresh = useCallback(async (): Promise<boolean> => {
    // Get fresh session data to ensure we have the latest token state
    // (another tab might have already refreshed the token)
    const currentSession = await getSession()

    // This shouldn't happen, and would cause a useless retry, but
    // need this to silence type checker.
    if (!currentSession?.tokens?.expiresAt) {
      return false
    }

    const { isExpired, secondsUntilExpiry } = calculateTokenExpiry(currentSession.tokens.expiresAt)
    const needsRefresh = isExpired || secondsUntilExpiry <= refreshThreshold

    if (!needsRefresh) {
      console.debug(`[Tab ${tabId}] Token no longer needs refresh (${secondsUntilExpiry}s remaining), skipping`)
      return true // Consider this success since token is fresh
    }

    refreshInProgress.current = true
    try {
      console.debug(`[Tab ${tabId}] Starting token refresh (${secondsUntilExpiry}s until expiry)`)

      try {
        const response = await fetch("/api/auth/refresh", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        })

        const result = await response.json()

        if (!response.ok || !result.success) {
          const error = result.error || "Token refresh failed"
          console.warn(`[Tab ${tabId}] Token refresh failed: ${error} (status: ${response.status})`)
          return false
        }

        // Success - update session with new tokens
        await updateSession({
          refreshedTokens: {
            access_token: result.access_token,
            refresh_token: result.refresh_token,
            expires_in: result.expires_in,
          },
        })

        console.debug(`[Tab ${tabId}] Token refresh successful, new token expires in ${result.expires_in}s`)

        // Reset failure counter on success
        failureCountRef.current = 0

        return true
      } catch (error) {
        console.warn(`[Tab ${tabId}] Token refresh failed with error:`, error)
        return false
      }
    } finally {
      refreshInProgress.current = false
    }
  }, [updateSession, tabId])

  const scheduleRefresh = useCallback(
    (reason = "normal") => {
      // Clear existing timeout
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
      if (!session?.tokens) {
        return
      }
      const expiresAt = session.tokens.expiresAt

      const { isExpired, secondsUntilExpiry } = calculateTokenExpiry(expiresAt)
      const expiryTime = new Date(expiresAt * 1000).toLocaleTimeString()

      console.debug(`[Tab ${tabId}] Token expires at ${expiryTime} (${secondsUntilExpiry}s from now)`)

      // Determine when to refresh
      let refreshIn: number = 0
      let logMessage: string

      if (isExpired) {
        logMessage = `[Tab ${tabId}] Token expired, refreshing immediately`
      } else {
        // Check if token expires soon
        if (secondsUntilExpiry <= refreshThreshold) {
          // Token expires soon, refresh immediately
          logMessage = `[Tab ${tabId}] Token expires soon (${secondsUntilExpiry}s), refreshing immediately`
        } else {
          // Schedule refresh before token expires with jitter
          refreshIn = Math.max(0, (secondsUntilExpiry - refreshThreshold) * 1000)

          // Add random jitter to prevent multiple tabs from refreshing at the same time
          // 10s max in dev, 60s max in prod
          const maxJitter = process.env.NODE_ENV === "development" ? 10 : 60
          const jitter = Math.random() * maxJitter * 1000
          refreshIn += jitter

          const refreshAtTime = new Date(Date.now() + refreshIn).toLocaleTimeString()
          logMessage = `[Tab ${tabId}] Scheduling ${reason} refresh in ${Math.round(refreshIn / 1000)}s (at ${refreshAtTime}) [+${Math.round(jitter / 1000)}s jitter]`
        }
      }

      console.debug(logMessage)

      // Make sure we have a minimum jitter to avoid the thundering herd problem.
      refreshIn = Math.max(refreshIn, 5000 + Math.random() * 5000)

      refreshTimeoutRef.current = setTimeout(async () => {
        const success = await performRefresh()

        if (!success) {
          // Increment failure counter
          failureCountRef.current += 1

          // Stop retrying after 2 consecutive failures
          if (failureCountRef.current < 2) {
            console.debug(`[Tab ${tabId}] Refresh failed (${failureCountRef.current}/2), scheduling retry`)
            scheduleRefresh("retry")
          } else {
            console.error(`[Tab ${tabId}] Token refresh failed 2 times consecutively, stopping retries`)
          }
        }
      }, refreshIn)
    },
    [performRefresh, tabId, session?.tokens]
  )

  // Monitor token expiration
  useEffect(() => {
    if (!session?.tokens?.expiresAt) {
      console.debug(`[Tab ${tabId}] No session tokens or expiresAt, clearing any scheduled refresh`)
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
      failureCountRef.current = 0
      return
    }

    if (failureCountRef.current > 2) {
      console.debug(`[Tab ${tabId}] ${failureCountRef.current} failures, not retrying`)
      return
    }

    // Schedule when to refresh our access token. Apparently we get
    // here too when our tokens are updated via performRefresh, so try
    // skipping rescheduling an in-progress event.
    if (!refreshInProgress.current) {
      scheduleRefresh("scheduled")
    } else {
      console.debug("Refresh in progress")
    }
  }, [session?.tokens?.expiresAt, scheduleRefresh, tabId])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      refreshInProgress.current = false
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
      failureCountRef.current = 0
    }
  }, [])

  return {
    refresh: performRefresh,
  }
}
